if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      // Define element properties
      #formWrapper;
      #watchTarget;
      #header;
      #stickyElement;
      #isVisible = false;
      #animation = null;
      #observer;
      #isAtBottom = false;
      #scrollHandler;

      constructor() {
        super();

        // Cache DOM elements
        this.#formWrapper = document.getElementById('product-form-wrapper');
        // 22 Oct 2026 (storefront review S2): watch the Add to cart form
        // itself. Watching the whole info column kept the bar hidden
        // until the description, nutrition and delivery had scrolled past.
        this.#watchTarget = this.#formWrapper.querySelector('product-form') || this.#formWrapper;
        this.#header = document.getElementById('Header');
        // sticky element is the html inside the template tag
        this.#stickyElement = this.firstElementChild;

        // Setup sticky element
        this.#stickyElement.id = 'sticky-atc';
        
        // Create bound scroll handler
        this.#scrollHandler = () => this.#handleScroll();
      }

      /**
       * Lifecycle: When element is added to DOM
       */
      connectedCallback() {
        this.#setupObserver();
        window.addEventListener('scroll', this.#scrollHandler, { passive: true });
      }

      /**
       * Lifecycle: When element is removed from DOM
       */
      disconnectedCallback() {
        this.#observer?.disconnect();
        this.#animation?.cancel();
        window.removeEventListener('scroll', this.#scrollHandler);
      }

      /**
       * Initialize Intersection Observer to watch form wrapper
       */
      #setupObserver() {
        const options = {
          threshold: 0,
          rootMargin: '-100px'
        };

        this.#observer = new IntersectionObserver(
          this.#handleIntersect.bind(this),
          options
        );
        this.#observer.observe(this.#watchTarget);
      }

      /**
       * Handle intersection changes
       */
      #handleIntersect(entries) {
        const [entry] = entries;
        const { bottom } = this.#watchTarget.getBoundingClientRect();
        const isFormAboveFold = bottom < 100;

        if (!entry.isIntersecting && isFormAboveFold) {
          this.#show();
        } else {
          this.#hide();
        }
      };

      /**
       * Show sticky element with animation
       */
      #show() {
        if (this.#isVisible) return;

        this.#stickyElement.classList.remove('translate-y-full', 'opacity-0');
        this.#isVisible = true;
      }

      /**
       * Hide sticky element with animation
       */
      #hide() {
        if (!this.#isVisible) return;

        this.#stickyElement.classList.add('translate-y-full', 'opacity-0');
        this.#isVisible = false;
      }

      /**
       * Handle window scroll events
       */
      #handleScroll() {
        if (!this.#stickyElement) return;

        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const footerHeight = 200; // Approximate footer height
        
        // Check if we're near the bottom (with footer consideration)
        const isAtBottom = (windowHeight + scrollTop) >= (documentHeight - footerHeight);
        
        if (isAtBottom !== this.#isAtBottom) {
          this.#isAtBottom = isAtBottom;
          
          if (isAtBottom) {
            this.#hide();
          } else if (!this.#isVisible) {
            // Only show if we were previously hidden by scroll and the form is above fold
            const { bottom } = this.#watchTarget.getBoundingClientRect();
            const isFormAboveFold = bottom < 100;
            if (isFormAboveFold) {
              this.#show();
            }
          }
        }
      }
    }
  );
}
